import { metrics } from '@opentelemetry/api';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { Logger } from '../logger';
import { getBrowserInfo } from 'utils/platform';

export const CONSTANTS = {
  USER_ID: 'user.id',
  USER_AGENT_NAME: 'user_agent.name',
  PAGE_URL: 'url.path_template',
};

// Normalizes the current pathname at call time — not at module load.
//
// WHY CALLED PER-METRIC AND NOT ONCE AT MODULE LOAD
// This file is imported once at app startup. In a SPA, window.location.pathname
// changes on every React Router navigation without a page reload, so a snapshot
// taken at import time would tag every subsequent metric with the initial path
// (often /login or /dashboard). Reading the URL inside buildAttributes ensures
// each metric is attributed to the page where it was actually measured.
//
// WHY NORMALIZE IDs
// /projects/123 and /projects/456 are the same page template. Without
// normalization they fan out into separate metric series per record ID, making
// aggregation (p75 LCP per page) impossible.
const getCurrentPageUrl = () =>
  window.location.pathname
    .replace(/\?.*$/, '') // strip query string
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '/:id',
    ) // UUID segments
    .replace(/\/\d+/g, '/:id'); // numeric segments

const meter = metrics.getMeter('web-vitals');
const lcp = meter.createHistogram('lcp');
const cls = meter.createObservableGauge('cls');
const inp = meter.createHistogram('inp');
const ttfb = meter.createHistogram('ttfb');
const fcp = meter.createHistogram('fcp');

// Builds the full attribute set for a metric at the moment it fires.
// page.title and url.path_template are read here (not at module load) so they
// reflect the current page — critical for INP and CLS which fire during
// navigation, not just on initial load.
function buildAttributes(metric: Metric) {
  return {
    [CONSTANTS.USER_AGENT_NAME]: getBrowserInfo(),
    [CONSTANTS.PAGE_URL]: getCurrentPageUrl(),
    'page.title': document.title,
    'web_vital.rating': metric.rating,
    'web_vital.id': metric.id,
    'web_vital.navigationType': metric.navigationType,
    'web_vital.delta': metric.delta,
  };
}

// CLS is cumulative — onCLS fires on every layout shift update.
// Store the latest value + attrs; the observable gauge reads them on each export cycle.
let latestCls: {
  value: number;
  attrs: ReturnType<typeof buildAttributes>;
} | null = null;
cls.addCallback((result) => {
  if (latestCls) {
    result.observe(latestCls.value, latestCls.attrs);
  }
});

function sendToAnalytics(metric: Metric) {
  const attrs = buildAttributes(metric);
  switch (metric.name) {
    case 'LCP': {
      lcp.record(metric.value, attrs);
      break;
    }
    case 'CLS': {
      latestCls = { value: metric.value, attrs };
      break;
    }
    case 'INP': {
      inp.record(metric.value, attrs);
      break;
    }
    case 'TTFB': {
      ttfb.record(metric.value, attrs);
      break;
    }
    case 'FCP': {
      fcp.record(metric.value, attrs);
      break;
    }
    default: {
      Logger.error({
        message: 'Unexpected metric name',
        data: metric,
      });
    }
  }
}

onCLS(sendToAnalytics);
onINP(sendToAnalytics);
onLCP(sendToAnalytics);
onTTFB(sendToAnalytics);
onFCP(sendToAnalytics);
